import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMediaQuery } from 'react-responsive';
import Layout from "../components/Layout";
import TripHeader from "../components/trips/TripHeader";
import TripSidebar from "../components/trips/TripSidebar";
import TripDayView from "../components/trips/TripDayView";
import useTripData from "../hooks/useTripData";
import EditItemModal from "../modals/EditItemModal";
import LodgingModal from "../components/LodgingModal";
import CommuteModal from "../components/CommuteModal";
import FlightsModal from "../components/FlightsModal";
import FlightForm from "../components/FlightForm";
import groupSubTripsByDay from "../utils/groupSubTripsByDay";
import TripToolbar from "../components/trips/TripToolbar";
import TripNote from "../components/trips/TripNote";
import CreateSubTrip from "../components/CreateSubTrip";
import AddTripMember from "../components/AddTripMember";
import AddExpense from "../components/AddExpense";
import MemberList from "../components/MemberList";
import ExpensesList from "../components/ExpensesList";
import SubTripModal from "../components/SubTripModal";
import { eachDayOfInterval, format, parseISO } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import Modal from "../components/Modal";
import ReactModal from "react-modal";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Wallet } from "lucide-react";

const EditTripPage = () => {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });  
  
  useEffect(() => {
    if (isMobile) setShowMenu(false);
  }, [isMobile]);  
  
  const { 
	trip, 
	setTrip, 
	subTrips, 
	fetchSubTrips, 
	subTripItems, 
	fetchAllSubTripItems, 
	fetchTripDetails,
	refreshSubTripData,
  } = useTripData(tripId);

  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showTripNoteEditor, setShowTripNoteEditor] = useState(false);

  const [editingSubTrip, setEditingSubTrip] = useState(null);
  const [showSubTripModal, setShowSubTripModal] = useState(false);

  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [memberRefreshKey, setMemberRefreshKey] = useState(0);
  const [expenseRefreshKey, setExpenseRefreshKey] = useState(0);

  const [isMember, setIsMember] = useState(false);

  const handleMemberAdded = () => setMemberRefreshKey((prev) => prev + 1);
  const handleExpenseAdded = () => setExpenseRefreshKey((prev) => prev + 1);
  
  const [selectedDay, setSelectedDay] = useState(null);
  const [showMenu, setShowMenu] = useState(true);

  const [expandedDays, setExpandedDays] = useState({});
  const [showMemberModal, setShowMemberModal] = useState(false);
  
  const [setSubTripItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [editingItemData, setEditingItemData] = useState({});
  
  const [showExpenseListModal, setShowExpenseListModal] = useState(false);
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);  
  
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showFlightsModal, setShowFlightsModal] = useState(false);
  const [showLodgingModal, setShowLodgingModal] = useState(false);
  const [showCommuteModal, setShowCommuteModal] = useState(false);
  const [showRentalModal, setShowRentalModal] = useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);  
  const [showOtherModal, setShowOtherModal] = useState(false);    

  const [activeSubTripId, setActiveSubTripId] = useState(null);  

  const toggleDay = (day) => {
    setExpandedDays(prev => ({ ...prev, [day]: !prev[day] }));
  };

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);

  const formatTripDateRange = (startDate, endDate) => {
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    return `${start.getDate()}/${start.getMonth() + 1} - ${end.getDate()}/${end.getMonth() + 1}`;
  };

  useEffect(() => {
    if (trip?.start_date && trip?.end_date) {
      setDateRange([{
        startDate: new Date(trip.start_date),
        endDate: new Date(trip.end_date),
        key: "selection"
      }]);
    }
  }, [trip]);

   // Save Edited Field
  const saveField = async () => {
    if (!editingField) return;
    setIsSaving(true);

    const token = localStorage.getItem("token");
	// const body = { [editingField]: editingValue };
    const body = {
      name: editingField === "name" ? editingValue : trip.name,
      start_date: editingField === "start_date" ? editingValue : trip.start_date,
      end_date: editingField === "end_date" ? editingValue : trip.end_date,
	  note: trip.note || "",
    };

	if (!isMember) {
	  alert("You do not have permission to edit this trip.");
	  return;
	}

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/trips/update_trip/${tripId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

    if (!res.ok) {
      const error = await res.json();
      console.error("Update error details:", error);
      alert(`Update failed: ${JSON.stringify(error.detail)}`);
    }

	  setTrip((prev) => ({ ...prev, ...body }));
      setEditingField(null);
    } catch (err) {
      alert("Failed to update");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldClick = (field) => {
    setEditingField(field);
    setEditingValue(trip[field]);
  };

  const handleInputChange = (e) => setEditingValue(e.target.value);
  const handleInputBlur = () => saveField();
  const handleInputKeyDown = (e) => e.key === "Enter" && saveField();

	const handleSubTripSave = async (updatedSubTrip) => {
	  const token = localStorage.getItem("token");

	  const body = {
		date: updatedSubTrip.date,
		place: updatedSubTrip.place
	  };

	  try {
		await fetch(`${process.env.REACT_APP_API_URL}/subtrips/update_sub_trip/${updatedSubTrip.id}`, {
		  method: "PUT",
		  headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${token}`,
		  },
		  body: JSON.stringify(body),
		});

		await fetchSubTrips();
	  } catch {
		alert("Failed to update sub trip");
	  }
	};

const handleAddItem = (day) => {
  // const relatedSubTrip = subTrips.find(st =>
    // day >= st.start_date && day <= st.end_date
  // );
  const relatedSubTrip = subTrips.find(st =>
    format(new Date(st.date), "yyyy-MM-dd") === format(new Date(day), "yyyy-MM-dd")
  );

  if (!relatedSubTrip) {
    alert("No place found for this day. Please add one first.");
    return;
  }

  if (!isMember) {
    alert("You do not have permission to edit this trip.");
    return;
  }

  const newItem = {
    id: null,
    date: day,
    place: "",
	place_id:"",
	lat:"",
	lng:"",
    type: "",
    sub_trip_id: relatedSubTrip.id,
    details: "",
    start_time: "",
    end_time: "",
    cost: "",
    url: ""
  };

  setEditingItem(newItem);        // 開啟 modal
  setEditingItemData(newItem);   // 初始值
};

const handleDeleteItem = async (itemId) => {
  if (!window.confirm("Delete this item?")) return;

  const res = await fetch(`${process.env.REACT_APP_API_URL}/subtripitems/sub_trip/item/${itemId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`
    }
  });

  if (res.ok) {
    await fetchAllSubTripItems();
  } else {
    alert("Failed to delete item.");
  }
};

const handleEditItem = (item) => {
  setEditingItem(item);
  setEditingItemData({ ...item }); // clone item
};

const handleChangeItemField = (field, value) => {
  setEditingItemData((prev) => ({ ...prev, [field]: value }));
};

const handleSaveItem = async () => {
  if (!editingItemData.place || !editingItemData.type) {
    alert("Place and Type are required");
    return;
  }

  const isNew = !editingItemData.id;
  const endpoint = isNew
    ? `${process.env.REACT_APP_API_URL}/subtripitems/sub_trip/${editingItemData.sub_trip_id}/item`
    : `${process.env.REACT_APP_API_URL}/subtripitems/sub_trip/item/${editingItemData.id}`;

  const method = isNew ? "POST" : "PUT";

  const res = await fetch(endpoint, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(editingItemData),
  });

  if (res.ok) {
    await fetchAllSubTripItems();
    setEditingItem(null);
  } else {
    alert("Failed to save item");
  }
};

  const handleDeleteSubTrip = async (subTripId) => {
    if (!window.confirm("Delete this Sub Trip and all its items?")) return;

    const token = localStorage.getItem("token");
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/subtrips/delete_sub_trip/${subTripId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      await fetchSubTrips();
    } catch {
      alert("Deletion failed, please try again later");
    }
  };

  const handleDeleteTrip = async () => {
    if (!window.confirm(
      "Are you sure you want to delete this Trip?\n\nThis will delete all sub-trips, items, members, and expenses!"
    )) return;

    const token = localStorage.getItem("token");

    try {
      await fetch(`${process.env.REACT_APP_API_URL}/trips/delete_trip/${tripId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Trip deleted successfully.");
      navigate("/dashboard");
    } catch (error) {
      alert("Failed to delete trip. Please try again later.");
    }
  };

	useEffect(() => {
	  const checkMembership = async () => {
		const token = localStorage.getItem("token");

		const res = await fetch(`${process.env.REACT_APP_API_URL}/trips/${tripId}/is_member`, {
		  headers: {
			Authorization: `Bearer ${token}`
		  }
		});

		if (res.ok) {
		  const data = await res.json();
		  setIsMember(data.is_member);
		} else {
		  setIsMember(false);
		}
	  };

	  fetchTripDetails();
	  fetchSubTrips();
	  checkMembership();
	}, [fetchTripDetails, fetchSubTrips, tripId]);

  return (
	<Layout>
	  <div className="bg-background min-h-screen p-6">
		  {/* Sticky Top Trip Header */}
			{trip && (
			  <TripHeader
				trip={trip}
				tripId={tripId}
				navigate={navigate}
				editingField={editingField}
				editingValue={editingValue}
				showDatePicker={showDatePicker}
				dateRange={dateRange}
				memberRefreshKey={memberRefreshKey}
				setEditingField={setEditingField}
				setEditingValue={setEditingValue}
				setShowDatePicker={setShowDatePicker}
				setShowMemberModal={setShowMemberModal}
				handleInputChange={handleInputChange}
				handleInputBlur={handleInputBlur}
				handleInputKeyDown={handleInputKeyDown}
				saveField={saveField}
				setTrip={setTrip}
			  />
			)}  
		  {/* Sub Trips */}
			<section className="flex flex-col md:flex-row">
			  {/* 左側浮動日期欄 */}

				  <TripSidebar
					trip={trip}
					subTrips={subTrips}
					selectedDay={selectedDay}
					setSelectedDay={setSelectedDay}
					showMenu={showMenu}
					setShowMenu={setShowMenu}
				  />

			  {/* 主內容，調整 padding-left 以避開側欄 */}
			  <div className="flex-1 space-y-4 px-4 sm:px-6 md:px-12 pt-4">	
				{/* Add Sub Trips */}
				<section className="space-y-3">
					{isMember && (
					  <TripToolbar
						onOpenNote={() => setShowNoteModal(true)}
						onOpenMembers={() => setShowMemberModal(true)}
						onOpenExpenses={() => setShowExpenseListModal(true)}
						onOpenFlights={() => setShowFlightsModal(true)}
						onOpenLodging={() => {
							if (subTrips.length > 0) {
							  setActiveSubTripId(subTrips[0].id);
							  setShowLodgingModal(true);
							}
						  }}
						onOpenCommute={() => {
							if (subTrips.length > 0) {
							  setActiveSubTripId(subTrips[0].id);
							  setShowCommuteModal(true);
							}
						  }}						  
						onOpenRental={() => setShowRentalModal(true)}
						onOpenAttachments={() => setShowAttachmentModal(true)}
						onOpenOther={() => setShowOtherModal(true)}
						className="flex flex-wrap gap-2"
					  />				  
					)}

				</section>

				<h2 className="text-2xl font-bold text-darkgreen mb-2">Itinerary</h2>
					{selectedDay ? (
					  <TripDayView
						day={selectedDay}
						items={subTripItems.filter(item => item.date === selectedDay)}
						onAddItem={handleAddItem}
						onEditItem={handleEditItem}
						onDeleteItem={handleDeleteItem}
						subTrips={subTrips}
						onEditSubTrip={handleSubTripSave}
						onDeleteSubTrip={handleDeleteSubTrip}
					  />
					) : (
					  <p className="italic text-gray-500">Please select a day to view items.</p>
					)}
			  </div>
			</section>			
		  {/* Delete Trip Button */}
		  <div className="pt-8 text-right">
			<button
			  className="w-full sm:w-auto bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
			  onClick={handleDeleteTrip}
			>
			  Delete This Trip
			</button>
		  </div>
		</div>
	  {/* Modal */}
		<Modal isOpen={showNoteModal} onClose={() => setShowNoteModal(false)}>
		  {trip ? (
			<TripNote trip={trip} setTrip={setTrip} onClose={() => {setShowNoteModal(false); refreshSubTripData();}} />
		  ) : (
			<p className="text-gray-400 italic">Loading trip data...</p>
		  )}
		</Modal>
		<Modal isOpen={showRentalModal} onClose={() => {setShowRentalModal(false); refreshSubTripData();}}>
		  <h3 className="text-xl font-semibold text-primary mb-4">Rental Car</h3>
		  <p>Feature coming soon...</p>
		</Modal>

		<Modal isOpen={showAttachmentModal} onClose={() => {setShowAttachmentModal(false); refreshSubTripData();}}>
		  <h3 className="text-xl font-semibold text-primary mb-4">Attachments</h3>
		  <p>Feature coming soon...</p>
		</Modal>  
		
		<Modal isOpen={showOtherModal} onClose={() => {setShowOtherModal(false); refreshSubTripData();}}>
		  <h3 className="text-xl font-semibold text-primary mb-4">Other</h3>
		  <p>Feature coming soon...</p>
		</Modal>  		

		<Modal isOpen={showMemberModal} onClose={() => {setShowMemberModal(false); refreshSubTripData();}}>
		  <h3 className="text-xl font-semibold mb-4 text-primary">Manage Trip Members</h3>
		  <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
			<MemberList tripId={tripId} refreshKey={memberRefreshKey} />
			<AddTripMember tripId={tripId} onMemberAdded={handleMemberAdded} />
		  </div>
		</Modal>	
		<EditItemModal
		  isOpen={!!editingItem}
		  itemData={editingItemData}
		  onChange={handleChangeItemField}
		  onClose={() => {
			setEditingItem(null);
			refreshSubTripData();
		  }}
		  onSave={handleSaveItem}
		/>	
		<Modal isOpen={showExpenseListModal} onClose={() => {setShowExpenseListModal(false); refreshSubTripData();}}>
		  <div className="flex justify-between items-center mb-4">
			<h3 className="text-xl font-semibold text-primary">Expenses</h3>
			<button
			  onClick={() => {
				setShowExpenseListModal(false);
				setShowAddExpenseModal(true);
			  }}
			  onClose={() => {refreshSubTripData();}}
			  className="bg-green-500 max-h-[70vh] overflow-y-auto text-white px-3 py-1 rounded hover:bg-green-600"
			>
			  [+] Add
			</button>
		  </div>
		  <ExpensesList tripId={tripId} key={expenseRefreshKey} />
		</Modal>	
		<Modal isOpen={showAddExpenseModal} onClose={() => setShowAddExpenseModal(false)}>
		  <h3 className="text-xl font-semibold text-primary mb-4">Add New Expense</h3>
		  <AddExpense
			tripId={tripId}
			onExpenseAdded={() => {
			  setExpenseRefreshKey((prev) => prev + 1);
			  setShowAddExpenseModal(false);
			  setShowExpenseListModal(true);
			}}
		  />
		</Modal>
		{showFlightsModal && (
		  <FlightsModal
			isOpen={showFlightsModal}
			onClose={() => {
				setShowFlightsModal(false);
				refreshSubTripData();
			}}
			tripId={tripId}
		  />
		)}	
		<LodgingModal
		  isOpen={showLodgingModal}
		  onClose={() => {
			setShowLodgingModal(false);
			refreshSubTripData();
		  }}
		  subTripId={activeSubTripId}
		  parentTripId={tripId}
		/>		
		<Modal isOpen={showCommuteModal} onClose={() => setShowCommuteModal(false)}>
		  <CommuteModal
			isOpen={true}
			onClose={() => {
				setShowCommuteModal(false);
				refreshSubTripData();
			}}
			subTripId={activeSubTripId}
			parentTripId={tripId}
		  />
		</Modal>	
	</Layout>
  );
};

export default EditTripPage;
